import type { TFunction } from 'i18next';

import { EBlackbaudRaisersEdgeUnsubscribeSyncActions } from '@feathr/blackbox';
import type { IIntegrationUIConfig } from '@feathr/components';

/** Settings field keys for the unsubscribe preference. They match `IBlackbaudUnsubscribeSyncPreference`. */
export const UNSUBSCRIBE_ACTION_KEY = 'action';
export const SOLICIT_CODE_KEY = 'solicit_code';

/** Activity toggle keys that map to model attributes. The Action toggles have none yet. */
export const GIFT_DOWNSYNC_KEY = 'gift_events';
export const DONATION_UPSYNC_KEY = 'donations';

/** Raiser's Edge NXT v2 page config. The mapping region is set when that tab lands. */
export function getRaisersEdgeConfig(t: TFunction): IIntegrationUIConfig {
  const upsyncUnavailable = t('Syncing activity to Blackbaud is not available yet.');

  return {
    crmName: 'Blackbaud',
    mapping: { fields: [] },
    settingsSections: [
      {
        key: 'unsubscribes',
        heading: t('Manage unsubscribes'),
        description: t(
          'Select what should happen in Blackbaud when someone unsubscribes from an email in Feathr.',
        ),
        fields: [
          {
            key: UNSUBSCRIBE_ACTION_KEY,
            type: 'radio',
            label: t('When someone unsubscribes'),
            // The section heading names the group, so Figma shows no field label.
            hideLabel: true,
            options: [
              {
                id: EBlackbaudRaisersEdgeUnsubscribeSyncActions.DoNotSync,
                name: t('Do nothing in Blackbaud'),
                description: t(
                  'The constituent record in Blackbaud is left as is. Feathr keeps track of the unsubscribe on its own.',
                ),
              },
              {
                id: EBlackbaudRaisersEdgeUnsubscribeSyncActions.RequestsNoEmail,
                name: t('Add “Requests No Email” checkbox to constituent'),
                description: t(
                  'Checks the "Requests No Email" box on the constituent record in Blackbaud.',
                ),
              },
              {
                id: EBlackbaudRaisersEdgeUnsubscribeSyncActions.SolicitCode,
                name: t('Add solicit code to constituent'),
                description: t(
                  'Adds the solicit code you choose below to the constituent record in Blackbaud.',
                ),
              },
            ],
          },
          {
            key: SOLICIT_CODE_KEY,
            type: 'select',
            label: t('Solicit code'),
            placeholder: t('Select a code'),
            dependsOn: {
              key: UNSUBSCRIBE_ACTION_KEY,
              equals: EBlackbaudRaisersEdgeUnsubscribeSyncActions.SolicitCode,
            },
          },
        ],
      },
    ],
    setupSteps: [],
    tabs: [
      { key: 'mappings', label: t('Mappings'), path: 'mapping' },
      { key: 'activities', label: t('Activities'), path: 'activities' },
      { key: 'settings', label: t('Settings'), path: 'settings' },
    ],
    title: t('Blackbaud Raiser’s Edge NXT'),
    toggleSections: [
      {
        crmRecordLabel: t('Actions'),
        description: t('Choose which activities sync to the matching constituent above.'),
        heading: t('Activities'),
        key: 'actions',
        syncRule: 'up_sync',
        toggles: [
          {
            description: t('Email opens sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'email_open',
            label: t('Email open'),
          },
          {
            description: t('Email clicks sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'email_click',
            label: t('Email click'),
          },
          {
            description: t('Form submissions sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'form_submission',
            label: t('Form submission'),
          },
          {
            description: t('Ad clicks sync to the matching Blackbaud constituent.'),
            disabledReason: upsyncUnavailable,
            key: 'ad_click',
            label: t('Ad click'),
          },
        ],
      },
      {
        crmRecordLabel: t('Gifts'),
        description: t('Sync gift and donation activity with Blackbaud.'),
        heading: t('Donations'),
        key: 'gifts',
        syncRule: 'both_ways',
        toggles: [
          {
            description: t('Blackbaud gifts sync to the matching Feathr person record.'),
            key: GIFT_DOWNSYNC_KEY,
            label: t('Sync Blackbaud gifts to Feathr'),
            name: 'gift-downsync-card',
          },
          {
            description: t(
              'Feathr donations create or update the matching Blackbaud constituent and gift. Requires a fund.',
            ),
            key: DONATION_UPSYNC_KEY,
            label: t('Sync Feathr donations to Blackbaud'),
            name: 'donation-upsync-card',
          },
        ],
      },
    ],
  };
}
